import { useState } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Button,
  Collapse,
  Stack,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { isEmpty } from "lodash";
import { formatRemainingTime } from "../../utils/utils";
import { t } from "i18next";

function Spinner() {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 12,
        height: 12,
        border: "1.5px solid #BBDEFB",
        borderTopColor: "primary.main",
        borderRadius: "50%",
        flexShrink: 0,
        animation: "spin 0.8s linear infinite",
        "@keyframes spin": { to: { transform: "rotate(360deg)" } },
      }}
    />
  );
}

function CheckIcon() {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 14,
        height: 14,
        borderRadius: "50%",
        bgcolor: "success.dark",
        color: "#fff",
        fontSize: 8,
        flexShrink: 0,
      }}
    >
      ✓
    </Box>
  );
}

function DotIcon() {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: "grey.400",
        flexShrink: 0,
      }}
    />
  );
}
function getProgressAndETA({
  result,
  prevResult,
  imgPerEpoch,
}: {
  result: resultItemType;
  prevResult?: resultItemType;
  imgPerEpoch: number;
}) {
  if (result.state === "done") {
    return { progress: 1, remainingTime: 0, phase: "done" as const };
  }

  // 검증 중 — 학습은 끝났지만 아직 done 아님
  if (result.state === "valid") {
    return { progress: 1, remainingTime: 0, phase: "valid" as const };
  }

  // epoch 1
  if (result.epoch === 1) {
    if (result.num_train === 0) {
      return { progress: 0, remainingTime: 0, phase: "train" as const };
    }
    const progress = result.num_train / imgPerEpoch;
    const speed = result.sec_elapsed / result.num_train;
    const remainingImages = imgPerEpoch - result.num_train;
    const remainingTime = speed * remainingImages;
    return { progress, remainingTime, phase: "train" as const };
  }

  // epoch >= 2
  if (prevResult && prevResult.sec_elapsed > 0) {
    const expectedTotal = prevResult.sec_elapsed;
    const progress = Math.min(result.sec_elapsed / expectedTotal, 1);
    const remainingTime = Math.max(expectedTotal - result.sec_elapsed, 0);
    return { progress, remainingTime, phase: "train" as const };
  }

  return { progress: 0, remainingTime: 0, phase: "train" as const };
}

function EpochCard({
  result,
  prevResult,
  epochNum,
  imgPerEpoch,
  onSnapshot,
  onViewHeatmap,
}: {
  result: resultItemType | null;
  prevResult?: resultItemType;
  epochNum: number;
  imgPerEpoch: number;
  onSnapshot?: (result: resultItemType) => void;
  onViewHeatmap?: (result: resultItemType) => void;
}) {
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  if (!result) {
    return (
      <Box
        sx={{
          bgcolor: "grey.50",
          border: "0.5px solid",
          borderColor: "grey.200",
          borderRadius: 1,
          px: 1.25,
          py: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" fontWeight={500} sx={{ minWidth: 56 }}>
            {t("Epoch")} {epochNum}
          </Typography>
          <DotIcon />
          <Typography variant="caption" color="text.disabled">
            {t("WaitingMsg")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // const isDone = result.state === "done";

  const { progress, remainingTime, phase } = getProgressAndETA({
    result,
    prevResult,
    imgPerEpoch,
  });

  const isValidating = phase === "valid";
  const isDone = phase === "done";
  const hasPerfData = isDone && result.performance && !isEmpty(result.performance);

  const percent = Math.floor(progress * 100);
  // 저장된 모델(model_uuid)이 있는 epoch만 스냅샷을 볼 수 있다
  const canSnapshot = !!onSnapshot && !!result.model_uuid;
  // 히트맵은 학습/검증이 끝나 모델이 저장된 epoch만 (AI 서버가 그 모델로 추론한다)
  const canHeatmap = !!onViewHeatmap && !!result.model_uuid && isDone;
  const hasMenu = canSnapshot || canHeatmap;

  return (
    <Box
      onContextMenu={
        hasMenu
          ? (e) => {
              e.preventDefault();
              setMenuPos({ top: e.clientY, left: e.clientX });
            }
          : undefined
      }
      sx={{
        bgcolor: "grey.50",
        border: "0.5px solid",
        borderColor: "grey.200",
        borderRadius: 1,
        px: 1.25,
        py: 1,
        ...(hasMenu && { cursor: "context-menu" }),
      }}
    >
      <Menu
        open={!!menuPos}
        onClose={() => setMenuPos(null)}
        anchorReference="anchorPosition"
        anchorPosition={menuPos ?? undefined}
      >
        {canSnapshot && (
          <MenuItem
            sx={{ fontSize: 13 }}
            onClick={() => {
              setMenuPos(null);
              onSnapshot?.(result);
            }}
          >
            {t("ViewSnapshot")}
          </MenuItem>
        )}
        {canHeatmap && (
          <MenuItem
            sx={{ fontSize: 13 }}
            onClick={() => {
              setMenuPos(null);
              onViewHeatmap?.(result);
            }}
          >
            {t("ViewHeatmapWithModel")}
          </MenuItem>
        )}
      </Menu>
      {/* 상단 */}
      <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
        <Typography variant="caption" fontWeight={500} sx={{ minWidth: 56 }}>
          {t("Epoch")} {result.epoch}
        </Typography>

        {isDone ? <CheckIcon /> : <Spinner />}

        {isDone ? (
          <Typography variant="caption" color="success.dark">
            {t("Done")}
          </Typography>
        ) : isValidating ? (
          <Typography variant="caption" color="warning.dark">
            {t("ValidatingMsg")}
          </Typography>
        ) : (
          <Typography variant="caption" color="primary.dark">
            {percent}% · {t("Approximately")} {formatRemainingTime(remainingTime)} {t("Remaining")}
          </Typography>
        )}

        <Box sx={{ ml: "auto !important" }}>
          {result.model_uuid ? (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "success.50",
                color: "success.900",
                borderRadius: 0.5,
                px: 0.75,
                py: 0.25,
                fontSize: 10,
                fontFamily: "monospace",
              }}
            >
              💾 {result.model_uuid.slice(0, 8)}…
            </Box>
          ) : (
            <Typography variant="caption" color="text.disabled">
              {t("NotSavedMsg")}
            </Typography>
          )}
        </Box>
      </Stack>

      {/* Progress Bar */}
      {!isDone && (
        <Box
          sx={{
            mt: 0.5,
            height: 4,
            borderRadius: 2,
            bgcolor: "grey.200",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${percent}%`,
              height: "100%",
              bgcolor: isValidating ? "warning.main" : "primary.main",
              transition: "width 0.3s ease",
              ...(isValidating && {
                "@keyframes shimmer": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.45 },
                  "100%": { opacity: 1 },
                },
                animation: "shimmer 1.4s ease-in-out infinite",
              }),
            }}
          />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
        {t("Cumulative")} {result.num_train.toLocaleString()}
        {t("ImageCountUnit")}
      </Typography>
      {hasPerfData && (
        <Stack direction="row" spacing={0.75} mt={0.75}>
          {[
            {
              label: t("Overkill"),
              value: `${(Number(result.performance.overkill) * 100).toFixed(3)}%`,
            },
            {
              label: t("Missing"),
              value: `${(Number(result.performance.missing) * 100).toFixed(3)}%`,
            },
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{
                flex: 1,
                bgcolor: "background.paper",
                border: "0.5px solid",
                borderColor: "grey.200",
                borderRadius: 0.5,
                px: 0.75,
                py: 0.5,
                textAlign: "center",
              }}
            >
              <Typography
                display="block"
                sx={{
                  fontSize: 9,
                  color: "text.disabled",
                  lineHeight: 1.2,
                }}
              >
                {label}
              </Typography>
              <Typography
                display="block"
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export interface resultItemType {
  epoch: number;
  model_uuid: string;
  performance: {
    overkill: number;
    missing: number;
  };
  num_train: number;
  sec_elapsed: number;
  state: "done" | "train" | "valid" | "done";
}

export default function TrainingProgressDropdown({
  totalEpoch = 3,
  results = [] as resultItemType[],
  outerState = "epoch",
  imgPerEpoch = 1,
  onSnapshot,
  onViewHeatmap,
}: {
  totalEpoch?: number;
  results?: resultItemType[];
  outerState?: string;
  imgPerEpoch?: number;
  onSnapshot?: (result: resultItemType) => void;
  onViewHeatmap?: (result: resultItemType) => void;
}) {
  const [open, setOpen] = useState(false);

  const isFinished = outerState === "stop";
  const doneCount = results.filter((r) => r.state === "done").length;
  const pct = totalEpoch > 0 ? Math.round((doneCount / totalEpoch) * 100) : 0;

  const currentResult = results.find((r) => r.state === "train" || r.state === "valid") ?? null;
  const isValidating = currentResult?.state === "valid";

  return (
    <Box>
      {/* 진행바 스트립 */}
      <Stack
        id="epoch-train-progress"
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1.5,
          height: 36,
          borderBottom: "0.5px solid",
          borderColor: "grey.200",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary" noWrap>
          {t("TrainProgress")}
        </Typography>
        <CircularProgress size="10px" />

        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            bgcolor: "grey.100",
            "& .MuiLinearProgress-bar": {
              borderRadius: 2,
              bgcolor: isValidating ? "warning.main" : undefined,
              ...(isValidating && {
                "@keyframes shimmer": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.45 },
                  "100%": { opacity: 1 },
                },
                animation: "shimmer 1.4s ease-in-out infinite",
              }),
            },
          }}
        />

        {/* % 옆에 검증 중 뱃지 */}
        <Typography
          variant="caption"
          color={isValidating ? "warning.dark" : "text.secondary"}
          sx={{ minWidth: 28, textAlign: "right" }}
        >
          {pct}%
        </Typography>

        <Button
          size="small"
          variant="outlined"
          endIcon={
            <KeyboardArrowDownIcon
              sx={{
                fontSize: "12px !important",
                transition: "transform 0.2s",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          }
          onClick={() => setOpen((v) => !v)}
          sx={{
            minWidth: 0,
            height: 22,
            px: 0.75,
            fontSize: 11,
            color: "text.secondary",
            borderColor: "grey.300",
            whiteSpace: "nowrap",
            "&:hover": { borderColor: "grey.400", bgcolor: "grey.50" },
          }}
        >
          {open ? t("DetailClose") : t("DetailOpen")}
        </Button>
      </Stack>
      {open && (
        <Box
          sx={{
            position: "absolute",
            top: 36,
            left: 0,
            width: "100%",
            zIndex: 1200,
            bgcolor: "background.paper",
            boxShadow: 3,
            borderRadius: 1,
            border: "0.5px solid",
            borderColor: "grey.200",
          }}
        >
          <Box
            sx={{
              maxHeight: 280,
              overflowY: "auto",
              px: 1.5,
              py: 1.25,
              borderBottom: "0.5px solid",
              borderColor: "grey.200",
            }}
          >
            <Stack id="epoch-card" spacing={0.75}>
              {Array.from({ length: totalEpoch }, (_, i) => {
                const epochNum = i + 1;

                const result = results.find((r) => r.epoch === epochNum) ?? null;

                const prevResult = results.find((r) => r.epoch === epochNum - 1) ?? undefined;

                return (
                  <EpochCard
                    key={epochNum}
                    result={result}
                    prevResult={prevResult}
                    epochNum={epochNum}
                    imgPerEpoch={imgPerEpoch}
                    onSnapshot={onSnapshot}
                    onViewHeatmap={onViewHeatmap}
                  />
                );
              })}
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
}
